import React from "react";
import { useConfig, useQualification, useNewsletter } from "../../../../contexts/api";
import { useConfirmationModal } from "../../../../contexts/app/confirmation";
import { Permission, usePermissions } from "../../../../contexts/app/permissions";
import useAppSettings from "../../../../contexts/app/appSettings";
import type { NewsletterMapping } from "../../../../api/newsletter/Newsletter.typed";
import { NO_QUALIFICATION_VALUE } from "../googleGroupsConstants";

export function useGoogleGroupsPageState() {
  const { getGoogleLabels } = useConfig();
  const { getQualificationsList } = useQualification();
  const { getNewsletterMapping, updateNewsletterMapping } = useNewsletter();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const { config, reloadConfig } = useAppSettings();

  const canEdit = hasPermission(Permission.COMMUNICATION_GOOGLE_GROUPS_EDIT);
  const canViewGoogleConfig =
    hasPermission(Permission.CENTRE_INFO_VIEW) ||
    hasPermission(Permission.CENTRE_INFO_EDIT) ||
    hasPermission(Permission.COMMUNICATION_SETTINGS_VIEW) ||
    hasPermission(Permission.COMMUNICATION_SETTINGS_EDIT);
  const canEditGoogleConfig =
    hasPermission(Permission.CENTRE_INFO_EDIT) ||
    hasPermission(Permission.COMMUNICATION_SETTINGS_EDIT);

  const [qualificationsList, setQualificationsList] = React.useState<string[]>([]);
  const [newsletterMapping, setNewsletterMapping] = React.useState<NewsletterMapping>({});
  const [availableLabels, setAvailableLabels] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!config) void reloadConfig();
  }, [config, reloadConfig]);

  React.useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [labels, qualifications, mapping] = await Promise.all([
          getGoogleLabels(),
          getQualificationsList(),
          getNewsletterMapping(),
        ]);

        if (cancelled) return;

        const nextMapping = mapping ?? {};
        const mappedLabels = Object.keys(nextMapping);

        setQualificationsList([...(qualifications ?? []), NO_QUALIFICATION_VALUE]);
        setNewsletterMapping(nextMapping);
        setAvailableLabels((labels ?? []).filter((label) => !mappedLabels.includes(label)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getGoogleLabels, getNewsletterMapping, getQualificationsList]);

  const handleAddQualification = React.useCallback((label: string, qualification: string) => {
    setNewsletterMapping((prev) => {
      if (prev[label]?.includes(qualification)) return prev;

      return {
        ...prev,
        [label]: prev[label] ? [...prev[label], qualification] : [qualification],
      };
    });
  }, []);

  const handleRemoveQualification = React.useCallback((label: string, qualification: string) => {
    setNewsletterMapping((prev) => ({
      ...prev,
      [label]: (prev[label] || []).filter((item) => item !== qualification),
    }));
  }, []);

  const handleAddGroup = React.useCallback((label: string) => {
    setNewsletterMapping((prev) => ({
      ...prev,
      [label]: prev[label] ?? [],
    }));
    setAvailableLabels((prev) => prev.filter((item) => item !== label));
  }, []);

  const handleRemoveGroup = React.useCallback((label: string) => {
    setNewsletterMapping((prev) => {
      const next = { ...prev };
      delete next[label];

      return next;
    });
    setAvailableLabels((prev) => (prev.includes(label) ? prev : [...prev, label]));
  }, []);

  const getAvailableQualifications = React.useCallback(
    (label: string) => {
      const mapped = newsletterMapping[label] || [];

      return qualificationsList.filter((item) => !mapped.includes(item));
    },
    [newsletterMapping, qualificationsList],
  );

  const onSave = React.useCallback(async () => {
    if (saving) return;

    const ok = await showConfirmation(
      "Enregistrer les groupes Google",
      "Enregistrer le mapping des qualifications ?",
    );
    if (!ok) return;

    setSaving(true);
    try {
      await updateNewsletterMapping(newsletterMapping);
    } finally {
      setSaving(false);
    }
  }, [newsletterMapping, saving, showConfirmation, updateNewsletterMapping]);

  return {
    canEdit,
    loading,
    saving,
    newsletterMapping,
    availableLabels,
    handleAddQualification,
    handleRemoveQualification,
    handleAddGroup,
    handleRemoveGroup,
    getAvailableQualifications,
    onSave,
    config,
    canViewGoogleConfig,
    canEditGoogleConfig,
  };
}
